import { memo } from 'react';

import { Row } from '@tanstack/react-table';

import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { getTimeEntryStatusText } from '@/modules/common/components/molecules/badge/status-configs/time-entry-status-config';
import { getTimeOffStatusText } from '@/modules/common/components/molecules/badge/status-configs/time-off-status-config';

import { ApprovalProjectRow } from '../../../types/time-tracking-approvals';
import { asTimeEntry, asTimeOffEntry, isTimeEntryRow, isTimeOffRow } from './approval-row.helpers';

interface ApprovalStatusCellProps {
  row: Row<ApprovalProjectRow>;
}

/**
 * Approval status badge, shown for leaf rows only: worklog entries get the
 * time-entry badge, time-off rows get the time-off request status badge
 * (Approved / Pending). Aggregate rows render an em dash.
 *
 * `getTimeOffStatusText` resolves copy from the `authenticated.timeOff` namespace via
 * the global i18n instance. That namespace is preloaded by the page
 * (`TimeTrackingApprovalsPage`'s `useTranslation(['authenticated.timeTracking',
 * 'authenticated.timeOff'])`) so it's already available by the time this cell — which
 * only renders once the table has data — first mounts.
 */
function ApprovalStatusCellComponent({ row }: ApprovalStatusCellProps) {
  if (isTimeOffRow(row)) {
    const { status } = asTimeOffEntry(row);

    return (
      <StatusBadge
        type={StatusBadgeType.TimeOff}
        status={status}
        label={getTimeOffStatusText(status)}
      />
    );
  }

  if (!isTimeEntryRow(row)) {
    // eslint-disable-next-line i18next/no-literal-string
    return <span className="text-muted-foreground">—</span>;
  }

  const { status } = asTimeEntry(row);

  return (
    <StatusBadge
      type={StatusBadgeType.TimeEntry}
      status={status}
      label={getTimeEntryStatusText(status)}
    />
  );
}

/** Memoized so an unrelated row's selection toggle doesn't re-render every status cell. */
export const ApprovalStatusCell = memo(ApprovalStatusCellComponent);
